import React, { Suspense, useRef, useState, useEffect, useLayoutEffect } from "react";
import * as THREE from "three";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import { Canvas, useThree } from "@react-three/fiber";
import { OrbitControls, ContactShadows, Html, useGLTF, Bounds, useBounds, Center } from "@react-three/drei";
import { ArrowsOut, ArrowClockwise, Cube, Warning } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";

// Loads the uploaded GLB/GLTF model. Shadows + no frustum culling so the whole
// asset is always drawn; framing/centering is handled by <Center>/<Bounds>.
function Model({ url }) {
  const { scene } = useGLTF(url, true);
  useLayoutEffect(() => {
    scene.traverse((o) => {
      if (o.isMesh) {
        o.castShadow = true;
        o.receiveShadow = true;
        o.frustumCulled = false;
      }
    });
  }, [scene]);
  return <primitive object={scene} />;
}

// Neutral image-based lighting generated locally (no external HDR fetch) so
// PBR materials render correctly and the render loop can't crash on a blocked
// network request.
function StudioEnvironment() {
  const { scene, gl } = useThree();
  useEffect(() => {
    const pmrem = new THREE.PMREMGenerator(gl);
    const envMap = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    scene.environment = envMap;
    return () => {
      scene.environment = null;
      envMap.dispose();
      pmrem.dispose();
    };
  }, [scene, gl]);
  return null;
}

// Refits the camera to the model bounds on first load and whenever resetKey changes.
function Refit({ resetKey }) {
  const bounds = useBounds();
  useEffect(() => {
    bounds.refresh().clip().fit();
  }, [resetKey, bounds]);
  return null;
}

class ModelErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidUpdate(prev) {
    if (prev.url !== this.props.url && this.state.hasError) this.setState({ hasError: false });
  }
  render() { return this.state.hasError ? this.props.fallback : this.props.children; }
}

function Overlay({ children }) {
  return <Html center>{children}</Html>;
}

export default function Property3D({ modelUrl }) {
  const { t } = useTranslation();
  const wrapRef = useRef(null);
  const [isFs, setIsFs] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  useEffect(() => {
    const onChange = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const resetView = () => setResetKey((k) => k + 1);
  const toggleFullscreen = () => {
    const el = wrapRef.current;
    if (!el) return;
    if (!document.fullscreenElement) el.requestFullscreen?.();
    else document.exitFullscreen?.();
  };

  if (!modelUrl) {
    return (
      <div data-testid="property-3d-empty" className="h-[380px] w-full rounded-sm border border-dashed border-slate-300 bg-slate-50 flex flex-col items-center justify-center text-center px-6">
        <Cube size={40} className="text-slate-300 mb-3" aria-hidden="true" />
        <h4 className="font-serif text-lg text-slate-700">{t("no3dModelTitle", "No 3D model uploaded")}</h4>
        <p className="text-sm text-slate-400 mt-1 max-w-sm">{t("no3dModelHelp", "Upload a GLB or GLTF model to explore this property in interactive 3D.")}</p>
      </div>
    );
  }

  const errorFallback = (
    <Overlay>
      <div className="flex flex-col items-center text-center text-slate-500 w-56">
        <Warning size={28} className="text-amber-500 mb-2" />
        <div className="text-sm">{t("model3dLoadError", "This 3D model could not be loaded. It may be unsupported or corrupt.")}</div>
      </div>
    </Overlay>
  );

  return (
    <div ref={wrapRef} data-testid="property-3d" className={`relative w-full rounded-sm overflow-hidden border border-slate-200 bg-gradient-to-b from-[#f4f1ea] to-[#e8e2d6] ${isFs ? "h-screen" : "h-[380px]"}`}>
      <div className="absolute top-2 right-2 z-10 flex gap-1.5">
        <button type="button" onClick={resetView} data-testid="model-reset"
          aria-label={t("resetView", "Reset view")} title={t("resetView", "Reset view")}
          className="w-8 h-8 rounded-sm bg-white/90 hover:bg-white border border-slate-200 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C5A059]">
          <ArrowClockwise size={16} className="text-[#0B2545]" />
        </button>
        <button type="button" onClick={toggleFullscreen} data-testid="model-fullscreen"
          aria-label={t("fullscreen", "Fullscreen")} title={t("fullscreen", "Fullscreen")}
          className="w-8 h-8 rounded-sm bg-white/90 hover:bg-white border border-slate-200 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C5A059]">
          <ArrowsOut size={16} className="text-[#0B2545]" />
        </button>
      </div>

      <Canvas
        shadows
        dpr={[1, 2]}
        camera={{ position: [4, 3, 6], fov: 45, near: 0.01, far: 1000 }}
        gl={{ preserveDrawingBuffer: true, antialias: true }}
        style={{ touchAction: "none" }}
      >
        <StudioEnvironment />
        <ambientLight intensity={0.5} />
        <hemisphereLight args={[0xffffff, 0x8d8d8d, 0.6]} />
        <directionalLight position={[6, 10, 6]} intensity={1.4} castShadow
          shadow-mapSize-width={1024} shadow-mapSize-height={1024} />
        <directionalLight position={[-6, 4, -4]} intensity={0.5} />
        <directionalLight position={[0, 4, -8]} intensity={0.4} />

        <Suspense fallback={<Overlay><span className="text-slate-400 text-sm">{t("loading3dModel", "Loading 3D model…")}</span></Overlay>}>
          <Bounds fit clip observe margin={1.2}>
            <Refit resetKey={resetKey} />
            <Center>
              <ModelErrorBoundary url={modelUrl} fallback={errorFallback}>
                <Model url={modelUrl} />
              </ModelErrorBoundary>
            </Center>
          </Bounds>
        </Suspense>

        <ContactShadows position={[0, -1.5, 0]} opacity={0.3} scale={12} blur={2.5} far={4} />

        <OrbitControls
          makeDefault
          enableDamping
          dampingFactor={0.08}
          enableZoom
          enablePan
          enableRotate
          minDistance={0.3}
          maxDistance={200}
          mouseButtons={{ LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.PAN }}
          touches={{ ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_PAN }}
        />
      </Canvas>
    </div>
  );
}
